import type {
    HistoryLabelOption,
    HistoryPrediction,
} from '../../../types/history'
import type { ModelDisplayCategorySummary } from '../../../types/modelManagement'
import type {
    ValidationAnnotation,
    ValidationAssetStatus,
    ValidationBBox,
    ValidationRuleConfig,
} from '../../../types/modelValidation'

export const isValidationRuleConfigValid = (
    config: ValidationRuleConfig
): boolean =>
    Object.values(config.category_weights).reduce(
        (total, value) => total + value,
        0
    ) === 10000 &&
    Object.values(config.metric_weights).reduce(
        (total, value) => total + value,
        0
    ) === 10000

export const percentToBasisPoints = (value: number): number =>
    Math.round(value * 100)

export const basisPointsToPercent = (
    value?: number | null
): number | undefined =>
    value === null || value === undefined ? undefined : value / 100

const getCategoryName = (
    category: ModelDisplayCategorySummary,
    language: string
): string => {
    if (language.toLowerCase().startsWith('en')) {
        return category.display_name_en || category.display_name_zh_cn
    }
    if (language.toLowerCase().startsWith('zh-hk')) {
        return category.display_name_zh_hk || category.display_name_zh_cn
    }
    return category.display_name_zh_cn
}

export const buildValidationLabelOptions = (
    categories: ModelDisplayCategorySummary[],
    language: string
): HistoryLabelOption[] =>
    categories
        .sort((left, right) => left.sort_order - right.sort_order)
        .map((category, index) => {
            const name = getCategoryName(category, language)
            const categoryKey = category.key.trim()
            const chineseName = category.display_name_zh_cn.trim()
            const displayName =
                categoryKey && chineseName && categoryKey !== chineseName
                    ? `${categoryKey} · ${chineseName}`
                    : categoryKey || chineseName || name
            return {
                cls_id: index,
                class: name,
                color: category.color,
                display_name: displayName,
                eng_class: category.key,
                display_category_key: category.key,
                display_names: {
                    en: category.display_name_en,
                    'zh-CN': category.display_name_zh_cn,
                    'zh-HK': category.display_name_zh_hk,
                },
                sort_order: category.sort_order,
            }
        })

export const validationAnnotationsToPredictions = (
    annotations: ValidationAnnotation[],
    labelOptions: HistoryLabelOption[]
): HistoryPrediction[] =>
    annotations.map((annotation) => {
        const option = labelOptions.find(
            (item) =>
                item.display_category_key === annotation.display_category_key
        )
        return {
            class: option?.class || annotation.display_category_key,
            cls_id: option?.cls_id,
            confidence: 1,
            eng_class: annotation.display_category_key,
            display_category_key: annotation.display_category_key,
            bbox: annotation.bbox,
        }
    })

export const predictionsToValidationAnnotations = (
    predictions: HistoryPrediction[]
): Array<{
    annotation_id: string
    display_category_key: string
    bbox: ValidationBBox
}> =>
    predictions.flatMap((prediction, index) => {
        const displayCategoryKey =
            prediction.display_category_key || prediction.eng_class
        if (!displayCategoryKey || !prediction.bbox) {
            return []
        }
        return [
            {
                annotation_id: `annotation-${index + 1}`,
                display_category_key: displayCategoryKey,
                bbox: prediction.bbox,
            },
        ]
    })

export const formatBasisPoints = (value: number): string =>
    `${basisPointsToPercent(value)?.toFixed(2).replace(/\.00$/, '')}%`

export const getLatestValidationVersion = <T extends { version_no: number }>(
    versions: T[]
): T | undefined =>
    [...versions].sort((left, right) => right.version_no - left.version_no)[0]

type ValidationVersionLike = {
    status: ValidationAssetStatus
    version_no: number
}

const getLatestVersionWithStatus = <T extends ValidationVersionLike>(
    versions: T[],
    status: ValidationAssetStatus
): T | undefined =>
    getLatestValidationVersion(
        versions.filter((version) => version.status === status)
    )

export const getDraftValidationVersion = <T extends ValidationVersionLike>(
    versions: T[]
): T | undefined => getLatestVersionWithStatus(versions, 'draft')

export const getCurrentPublishedValidationVersion = <
    T extends ValidationVersionLike,
>(
    versions: T[],
    latestPublishedVersion?: number | null
): T | undefined =>
    versions.find(
        (version) =>
            version.status === 'published' &&
            version.version_no === latestPublishedVersion
    ) || getLatestVersionWithStatus(versions, 'published')

export const getPreferredViewedValidationVersion = <
    T extends ValidationVersionLike,
>(
    versions: T[],
    latestPublishedVersion?: number | null
): T | undefined =>
    getCurrentPublishedValidationVersion(versions, latestPublishedVersion) ||
    getDraftValidationVersion(versions) ||
    getLatestVersionWithStatus(versions, 'disabled')

export const getCurrentValidationVersion = <T extends ValidationVersionLike>(
    versions: T[],
    latestPublishedVersion?: number | null
): T | undefined =>
    getDraftValidationVersion(versions) ||
    getCurrentPublishedValidationVersion(versions, latestPublishedVersion) ||
    getLatestVersionWithStatus(versions, 'disabled')
